import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface MonitorInfo {
  id: number;
  name: string;
  status: string;
  type: string;
  query: string;
  creator: string;
  lastTriggeredAt: string;
}

export interface EnvironmentHealth {
  env: string;
  status: string;
  monitors: MonitorInfo[];
}

export interface ServiceHealthResponse {
  serviceName: string;
  overallStatus: string;
  environments: EnvironmentHealth[];
  monitorCount: number;
  alertingCount: number;
}

export interface SloInfo {
  id: string;
  name: string;
  targetPercentage: number;
  currentSli: number | null;
  errorBudgetRemaining: number | null;
  timeframe: string;
  type: string;
}

export interface SlosResponse {
  slos: SloInfo[];
}

export interface IncidentInfo {
  id: string;
  title: string;
  severity: string;
  status: string;
  commander: string;
  createdAt: string;
  url: string;
}

export interface IncidentsResponse {
  incidents: IncidentInfo[];
}

export interface DashboardUrlResponse {
  url: string;
  title: string;
}

export interface DatadogApi {
  getMonitors(serviceName: string): Promise<ServiceHealthResponse>;
  getSlos(serviceName: string, ids?: string[]): Promise<SlosResponse>;
  getIncidents(serviceName: string): Promise<IncidentsResponse>;
  getDashboardUrl(dashboardId: string): Promise<DashboardUrlResponse>;
}

export const datadogApiRef = createApiRef<DatadogApi>({
  id: 'plugin.datadog',
});

export class DatadogApiClient implements DatadogApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('datadog');
  }

  async getMonitors(serviceName: string): Promise<ServiceHealthResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/monitors/${encodeURIComponent(serviceName)}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ??
          `Failed to fetch monitors: ${response.status}`,
      );
    }
    return response.json();
  }

  async getSlos(serviceName: string, ids?: string[]): Promise<SlosResponse> {
    const baseUrl = await this.getBaseUrl();
    let url: string;
    if (ids && ids.length > 0) {
      url = serviceName
        ? `${baseUrl}/slos/${encodeURIComponent(serviceName)}?ids=${encodeURIComponent(ids.join(','))}`
        : `${baseUrl}/slos?ids=${encodeURIComponent(ids.join(','))}`;
    } else {
      url = `${baseUrl}/slos/${encodeURIComponent(serviceName)}`;
    }
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ??
          `Failed to fetch SLOs: ${response.status}`,
      );
    }
    return response.json();
  }

  async getIncidents(serviceName: string): Promise<IncidentsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/incidents/${encodeURIComponent(serviceName)}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ??
          `Failed to fetch incidents: ${response.status}`,
      );
    }
    return response.json();
  }

  async getDashboardUrl(dashboardId: string): Promise<DashboardUrlResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/dashboard-url/${encodeURIComponent(dashboardId)}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ??
          `Failed to fetch dashboard URL: ${response.status}`,
      );
    }
    return response.json();
  }
}
